<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：データ復旧画面
 *
 * 作成者：2023/11/07 ノア）楊
 * 最終修正者：2023/11/16 ノア）楊
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.companyInfo.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.dataRecovery.title') }}</span>
          </div>
        </div>
      </nav>
    </section>

    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container-content">
        <!-- 実行履歴 -->
        <div class="history">
          <div class="content">
            <div class="title">
              {{ $t('gpsTracker.dataRecovery.history') }}
            </div>
            <div class="table-div">
              <el-table
                table-layout="auto"
                border
                highlight-current-row
                :stripe="true"
                header-row-class-name="tti-master-table-header"
                :data="tableData"
                class="data-table"
                :empty-text="$t('messages.common.emptyTableData')"
                max-height="630"
              >
                <!-- 実行時間 -->
                <el-table-column prop="actionTime" :label="$t('gpsTracker.dataRecovery.executionTime')" width="170">
                </el-table-column>
                <!-- 実行者 -->
                <el-table-column prop="actionUser" :label="$t('gpsTracker.dataRecovery.executor')" width="125">
                </el-table-column>
                <!-- 復旧対象 -->
                <el-table-column
                  prop="actionTarget"
                  class="action-target-width"
                  :label="$t('gpsTracker.dataRecovery.restorationObject')"
                >
                </el-table-column>
                <!-- 復旧期間 -->
                <el-table-column prop="actionPeriod" :label="$t('gpsTracker.dataRecovery.recoveryPeriod')" width="181">
                </el-table-column>
                <!-- 実行状態 -->
                <el-table-column
                  prop="actionStatus"
                  :label="$t('gpsTracker.dataRecovery.executionStatus')"
                  width="70"
                  :formatter="actionStatusFormat"
                >
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- データ復旧 -->
        <div class="data-recovery">
          <div class="content">
            <div class="title">
              {{ $t('gpsTracker.dataRecovery.title') }}
            </div>
            <div class="tti-form-child tti-form-child-level-1">
              <el-form :model="dataRecover" ref="ruleFormRef" :rules="rules">
                <!-- 会社一覧 -->
                <div class="company-title">
                  <span class="required-star">*</span>
                  <span class="company-label">{{ $t('gpsTracker.dataRecovery.company') }}</span>
                </div>
                <el-table
                  border
                  ref="multipleTable"
                  class="table-scroll"
                  highlight-current-row
                  :stripe="true"
                  max-height="420"
                  header-row-class-name="tti-master-table-header"
                  :data="companyTableData"
                  tooltip-effect="dark"
                  @selection-change="handleSelectionChange"
                  :empty-text="$t('messages.common.emptyTableData')"
                >
                  <el-table-column type="selection" width="35" class="selectionWidth"> </el-table-column>
                  <!-- 契約会社名 -->
                  <el-table-column prop="companyName" :label="$t(`gpsTracker.dataRecovery.companyName`)">
                  </el-table-column>
                  <!-- 契約会社コード -->
                  <el-table-column prop="companyCode" :label="$t(`gpsTracker.dataRecovery.companyCode`)">
                  </el-table-column>
                  <!-- 契約会社Prefix名 -->
                  <el-table-column prop="companyPrefix" :label="$t(`gpsTracker.dataRecovery.prefix`)">
                  </el-table-column>
                </el-table>

                <!-- 復旧対象 -->
                <div class="data-recover-obj">
                  <span class="required-star-group">*</span>
                  <el-form-item :label="$t('gpsTracker.dataRecovery.object')" prop="radio" class="radio-obj">
                    <el-radio-group
                      v-model="dataRecover.recoverObject"
                      @change="clearSigfox"
                      class="radio-group"
                      class-name="required"
                    >
                      <el-radio :label="sigFoxIdStatus.ALL" class="radio-all">{{
                        $t('gpsTracker.dataRecovery.allSigfox')
                      }}</el-radio>
                      <div class="sigfox-line">
                        <el-radio :label="sigFoxIdStatus.SIGFOXID" class="radio-sigfox-id">{{
                          $t('gpsTracker.dataRecovery.sigfoxID')
                        }}</el-radio>
                      </div>
                    </el-radio-group>
                  </el-form-item>

                  <el-form-item
                    prop="sigfoxId"
                    class="input-sigfox-id"
                    v-if="dataRecover.recoverObject === sigFoxIdStatus.SIGFOXID"
                  >
                    <el-input
                      v-model="dataRecover.sigfoxId"
                      class="input-sigfox"
                      size="small"
                      v-tooltip="{
                        content: `${$t('gpsTracker.tooltipCommon.toolDataRecovery.sigfoxID')}`,
                        placement: 'bottom-start',
                        delay: { show: 1000, hide: 0 }
                      }"
                    >
                    </el-input>
                  </el-form-item>
                </div>

                <!-- データ復旧期間 -->
                <div class="data-recover-date-picker">
                  <span class="required-star-date">*</span>
                  <el-form-item :label="$t('gpsTracker.dataRecovery.period')" prop="datePicker" class="date-picker">
                    <el-date-picker
                      v-model="dataRecover.recoverStartDate"
                      type="date"
                      format="yyyy/MM/dd"
                      value-format="yyyy/MM/dd"
                      :editable="false"
                      :clearable="false"
                      :picker-options="pickerOptions"
                      size="small"
                      v-tooltip="{
                        content: `${$t('gpsTracker.tooltipCommon.toolDataRecovery.datePeriod', [
                          $t('gpsTracker.dataRecovery.recoverStartDate')
                        ])}`,
                        placement: 'bottom-start',
                        delay: { show: 1000, hide: 0 }
                      }"
                    ></el-date-picker>

                    <span class="conn">{{ '~' }}</span>
                    <el-date-picker
                      v-model="dataRecover.recoverEndDate"
                      type="date"
                      format="yyyy/MM/dd"
                      value-format="yyyy/MM/dd"
                      :editable="false"
                      :clearable="false"
                      :picker-options="pickerOptions"
                      size="small"
                      v-tooltip="{
                        content: `${$t('gpsTracker.tooltipCommon.toolDataRecovery.datePeriod', [
                          $t('gpsTracker.dataRecovery.recoverEndDate')
                        ])}`,
                        placement: 'bottom-start',
                        delay: { show: 1000, hide: 0 }
                      }"
                    ></el-date-picker>
                  </el-form-item>
                </div>
              </el-form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- フッター -->
    <div class="tti-bottom-right-area">
      <el-button
        class="tti-btn-default tti-btn-001"
        @click="onAddClick"
        v-tooltip="{
          content: `${$t('gpsTracker.tooltipCommon.toolDataRecovery.executionButton')}`,
          placement: 'bottom-start',
          delay: { show: 1000, hide: 0 }
        }"
        >{{ $t('gpsTracker.dataRecovery.executionButton') }}</el-button
      >
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'

import TOOL_COMPANY_BASE from '@/views/companyEditTool/TOOL_COMPANY_BASE.vue'
import { i18n } from '@/locales'
import { CompanyAddToolData, ToolCompanyMasterData, ToolDataRecovery } from '@/models'
import { getToolCompanyInfoData, getToolDataRecoverData, postDataRecovery } from '@/store/apis'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { ToolDataRecoveryActionStatus, ToolDataRecoveryObj } from '@/commons/constants'
import { isDateAfter, pickerOptions } from '@/utils/dateUtils'
import { RULES, VALID_PATTERN, validateService } from '@/service/validateService'
import { cloneDeep } from 'lodash'

@Component({
  name: 'TOOL_DATA_RECOVERY'
})
export default class TOOL_DATA_RECOVERY extends TOOL_COMPANY_BASE {
  pageId = 'TOOL_DATA_RECOVERY'

  /**
   * ログインフォームのref
   */
  @Ref() readonly ruleFormRef!: Form

  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * データ復旧データ
   */
  dataRecover = {
    companyList: [] as ToolCompanyMasterData[],
    recoverObject: ToolDataRecoveryObj.ALL,
    sigfoxId: '',
    recoverStartDate: '',
    recoverEndDate: ''
  }

  /**
   * 今日の日付
   */
  actionTime = ''

  /**
   * 古いデータ
   */
  oldData = cloneDeep(this.dataRecover)

  /**
   * メッセージ送信フォームのref
   */
  @Ref('multipleTable') readonly multipleTable!: Table

  /**
   * データ
   */
  tableData: ToolDataRecovery[] = []
  companyTableData: Array<ToolCompanyMasterData> = []

  /**
   * 会社一覧
   */
  companyNameList = [] as string[]
  companyCodeList = [] as string[]

  /**
   * 会社に所属全端末
   */
  AllSigfoxId = 'all'

  /**
   * 復旧対象ステータス
   */
  sigFoxIdStatus = ToolDataRecoveryObj

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_DATA_RECOVERY > mounted')
    this.fetchTableData()
    this.fetchCompanyData()
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<void> {
    try {
      this.$log.debug(`TOOL_DATA_RECOVERY > fetchTableData`)
      this.tableData = await getToolDataRecoverData()
    } catch (e) {
      this.$log.debug(`TOOL_DATA_RECOVERY > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * データ復旧初期化を実行する。
   */
  async fetchCompanyData(): Promise<void> {
    try {
      const param = {
        nameOrCode: ''
      }
      const data = (await getToolCompanyInfoData(param)) as CompanyAddToolData
      this.companyTableData = data.list as Array<ToolCompanyMasterData>
    } catch (e) {
      this.$log.debug(`TOOL_DATA_RECOVERY > fetchCompanyData: e=`, e)
      throw e
    }
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER > onBackButtonClick`)
    this.$router.back()
  }

  /**
   * 書式設定実行状態
   *
   * @param {ToolDataRecovery} row 表の列
   * @returns {string} 表の内容
   */
  actionStatusFormat(row: ToolDataRecovery): string {
    if (row.actionStatus === ToolDataRecoveryActionStatus.SUCCESS) {
      return i18n.t('gpsTracker.dataRecovery.success').toString()
    } else if (row.actionStatus === ToolDataRecoveryActionStatus.FAILURE) {
      return i18n.t('gpsTracker.dataRecovery.failture').toString()
    }
    return ''
  }

  /**
   * 会社一覧クリック時の処理
   *
   * @param { Array<ToolCompanyMasterData>} val チェックされたデータリスト
   */
  handleSelectionChange(val: Array<ToolCompanyMasterData>): void {
    this.dataRecover.companyList = val
  }

  /**
   * ラジオボタンクリック時の処理
   */
  clearSigfox(): void {
    this.dataRecover.sigfoxId = ''
    this.ruleFormRef.clearValidate()
  }

  /**
   * 実行ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onAddClick(): void {
    if (!this.correlationCheck()) return

    this.ruleFormRef.validate(valid => {
      if (!valid) return
      this.confirmMsgBox()
    })
  }

  /**
   * 実行ボタンクリック後の処理
   */
  confirmMsgBox(): void {
    this.companyNameList = []
    this.companyCodeList = []
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hour = String(now.getHours()).padStart(2, '0')
    const minute = String(now.getMinutes()).padStart(2, '0')
    const second = String(now.getSeconds()).padStart(2, '0')

    this.actionTime = `${year}/${month}/${day} ${hour}:${minute}:${second}`

    this.dataRecover.companyList.forEach(item => {
      this.companyNameList.push(item.companyName)
      this.companyCodeList.push(item.companyCode)
    })

    const confirmCallback = async () => {
      this.confirmButtonClick()
    }

    msgBoxService.confirm(
      `${i18n.t('gpsTracker.dataRecovery.msgCompanyName', [this.companyNameList.join(',')]).toString()}\n
       ${i18n
         .t(
           'gpsTracker.dataRecovery.msgRestorationObject',
           this.dataRecover.recoverObject === ToolDataRecoveryObj.ALL
             ? [i18n.t('gpsTracker.dataRecovery.allSigfox')]
             : [this.dataRecover.sigfoxId]
         )
         .toString()}\n
       ${i18n
         .t('gpsTracker.dataRecovery.msgRecoveryPeriod', [
           this.dataRecover.recoverStartDate,
           this.dataRecover.recoverEndDate === this.actionTime.split(' ')[0].toString()
             ? this.actionTime
             : this.dataRecover.recoverEndDate + ' ' + i18n.t('gpsTracker.dataRecovery.endTime')
         ])
         .toString()}
      `,
      confirmCallback
    )
  }

  /**
   * クリック確認後の処理
   */
  async confirmButtonClick(): Promise<void> {
    try {
      const param = {
        companyCode: this.companyCodeList.join(','),
        companyName: this.companyNameList.join(','),
        sigfoxId: this.dataRecover.recoverObject === ToolDataRecoveryObj.ALL ? 'all' : this.dataRecover.sigfoxId,
        startDate: this.dataRecover.recoverStartDate + ' ' + i18n.t('gpsTracker.dataRecovery.startTime'),
        endDate:
          this.dataRecover.recoverEndDate === this.actionTime.split(' ')[0].toString()
            ? this.actionTime
            : this.dataRecover.recoverEndDate + ' ' + i18n.t('gpsTracker.dataRecovery.endTime')
      }
      await postDataRecovery(param)
      msgBoxService.info(i18n.t('gpsTracker.dataRecovery.dataSaved').toString())
      this.fetchTableData()
      this.dataRecover = { ...this.oldData }
      this.multipleTable.clearSelection()
    } catch (e) {
      this.$log.debug(`TOOL_DATA_RECOVERY > fetchCompanyData: e=`, e)
      this.fetchTableData()
      this.dataRecover = { ...this.oldData }
      this.multipleTable.clearSelection()
      throw e
    }
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    if (this.dataRecover.companyList.length === 0) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('gpsTracker.dataRecovery.company')]).toString()
      )
      return false
    }

    if (!this.dataRecover.recoverStartDate) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('gpsTracker.dataRecovery.recoverStartDate')]).toString()
      )
      return false
    }

    if (!this.dataRecover.recoverEndDate) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('gpsTracker.dataRecovery.recoverEndDate')]).toString()
      )
      return false
    }

    if (isDateAfter(this.dataRecover.recoverStartDate, this.dataRecover.recoverEndDate)) {
      msgBoxService.info(
        i18n
          .t('vaildate.common.dateRange', [
            i18n.t('gpsTracker.dataRecovery.recoverStartDate'),
            i18n.t('gpsTracker.dataRecovery.recoverEndDate')
          ])
          .toString()
      )
      return false
    }
    return true
  }

  /**
   * 重複チェック
   *
   * @param {string} value チェック対象
   * @returns {boolean} 重複がない
   */
  checkUserIdRepeat(value: string): boolean {
    const arr = value.split(',')
    const totalLength = arr.length
    const uniqueLength = new Set(arr).size
    if (totalLength === uniqueLength) {
      return true
    }
    return false
  }

  /**
   * 入力ボックスチェック
   */
  rules: Form['rules'] = {
    sigfoxId: [
      {
        required: true,
        message: i18n.t('gpsTracker.tooltipCommon.toolDataRecovery.sigfoxID').toString(),
        trigger: 'blur'
      },
      {
        pattern: VALID_PATTERN[RULES.SIGFOXID],
        message: i18n.t('vaildate.common.invalidSigfoxId').toString(),
        trigger: 'blur'
      },
      {
        validator: validateService.getValidator([
          {
            rule: RULES.CUSTOM,
            /* eslint-disable  @typescript-eslint/no-unused-vars */
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            callback: (value: string, rule: any): string | undefined => {
              if (!value) return
              const isError = this.checkUserIdRepeat(value)
              if (!isError) {
                const message = i18n.t('vaildate.common.sigfoxIDRepeatData').toString()
                return message
              }
            }
          }
        ]),
        trigger: 'blur'
      }
    ]
  }
}
</script>
<style lang="scss" scoped>
::v-deep .cell {
  white-space: pre-wrap;
}

.tti-bottom-right-area {
  position: relative;
}

.tti-btn-default {
  position: absolute;
  right: 0;
  margin: 10px 24px 0 0;
}

.data-recovery {
  color: $C08;
  width: 48%;
  background: $C01;
}

.table-scroll {
  width: 100%;

  ::v-deep .cell {
    white-space: pre;
    text-overflow: clip;
  }
}

.history {
  color: $C08;
  width: 48%;
  background: $C01;
}

.content {
  padding: 20px;

  .company-title {
    padding: 10px 0 5px 0;
    display: flex;

    .company-label {
      margin-left: 6px;
      font-size: 14px;
      color: $C12;
      margin-top: -4px;
    }
  }

  .table-div {
    .data-table {
      width: 100%;
      margin-top: 20px;
    }
  }
}

.required-star {
  color: $C21;
}

.title {
  border-bottom: 1px solid $C05;
}

.input {
  width: 150px;
  margin-left: 10px;
}

.conn {
  font-size: large;
  margin-left: 10px;
  margin-right: 10px;
}

.data-recover-obj {
  margin-top: 5px;
  height: 113px;
  display: flex;

  .required-star-group {
    color: $C21;
    margin-top: 14px;
  }

  .radio-obj {
    margin-left: 6px;
  }

  .radio-group {
    display: flex;
    flex-direction: column;

    .radio-all {
      display: block;
      margin-top: 40px;
    }

    .sigfox-line {
      display: flex;

      .radio-sigfox-id {
        margin-top: 15px;
      }
    }
  }
}

.data-recover-date-picker {
  display: flex;

  .required-star-date {
    color: $C21;
    margin-top: 35px;
  }

  .date-picker {
    display: flex;
    margin-top: 20px;
    margin-left: 6px;
  }
}

.input-sigfox-id {
  margin-left: 50px;
  margin-top: 58px;

  .input-sigfox {
    width: 240px;
  }
}

.tti-lr-container-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: calc(100vh - 170px);
  width: 100%;

  .left,
  .right {
    width: calc((100% - 24px) / 2);
  }

  .expander {
    margin-right: 4px !important;
    cursor: pointer;
  }

  .content {
    color: $C08;
    background: $C01;
    padding: 12px;
    overflow: auto;
  }
}
</style>
